import React from "react";
import { shallowEqual } from "react-redux";
import { CommonMonthlyPriceLabelKey } from "../../../../../../common-deprecated/priceUtils";
import { useUscSelector } from "../../../../redux/store";
import { formatMonthlyFinancePrice } from "../../../../../shared-logic/utils/uscPriceUtils";
import { UscDisclaimerType } from "../../../../../shared-logic/types/UscCommonTypes";
import LinkTextDisclaimer from "../LinkTextDisclaimer";
import * as Styles from "./UscMonthlyPriceDisclaimer.styles";
import type { TextVariantsType } from "../../styles/atoms/Text";
import Text from "../../styles/atoms/Text";
import { monthlyPriceFormatSelector } from "../../../../utils/helpers";
import DisclaimerIconWrapper from "../DisclaimerIconWrapper";

type UscMonthlyPriceType = {
    isPlaceholder?: boolean;
};

const UscMonthlyPriceDisclaimer = (props: UscMonthlyPriceType): JSX.Element | null => {
    const { isPlaceholder } = props;
    const result = useUscSelector((state) => state.vehicle.result);
    const financeInfo = useUscSelector((state) => state.finance.info);
    const monthlyPrice = useUscSelector((state) => state.finance.monthlyPrice);
    const commonSettings = useUscSelector((state) => state.commonSettings);
    const hideFinanceTogglePrices = useUscSelector((state) => state.uscSettings.hideFinanceTogglePrices);
    const monthlyPriceFormat = useUscSelector(monthlyPriceFormatSelector, shallowEqual);

    // When we're rendering the placeholder, we don't have the actual monthly price
    // So we use the default finance info (included in the vehicle response) to show a blurred version of the monthly price
    const financeInfoToUse = isPlaceholder ? result?.financeInfo || null : financeInfo;
    const monthlyPriceToUse = isPlaceholder ? result?.monthlyPrice || 0 : monthlyPrice;

    const { monthlyFinanceLabels, euriborFinanceLabels } = financeInfoToUse
        ? formatMonthlyFinancePrice(monthlyPriceToUse, commonSettings, financeInfoToUse, monthlyPriceFormat)
        : { monthlyFinanceLabels: [], euriborFinanceLabels: [] };

    const hasDisclaimers = !!monthlyFinanceLabels?.length || !!euriborFinanceLabels?.length;

    const textVariant: TextVariantsType = useUscSelector((state) =>
        state.uscSettings.financeLabelsUseBoldText ? "body-semibold" : "body-small-regular",
    );

    // The component UscInlinePrices handles the monthly price disclaimers in case the prices are hidden in the finance toggle
    if (!hasDisclaimers || hideFinanceTogglePrices) return null;

    return (
        <>
            <Styles.MonthlyWrapper isPlaceholder={isPlaceholder}>
                {!!monthlyFinanceLabels?.length && (
                    <>
                        <Text tag="div" variant={textVariant} color="grey-7">
                            {monthlyFinanceLabels.map(({ key, value }) => {
                                switch (key) {
                                    case CommonMonthlyPriceLabelKey.Price:
                                        return (
                                            <Text tag="span" variant={textVariant} color="grey-7" key={key}>
                                                <span dangerouslySetInnerHTML={{ __html: value }} />{" "}
                                            </Text>
                                        );

                                    case CommonMonthlyPriceLabelKey.Disclaimer:
                                        return (
                                            <Text tag="span" variant={textVariant} color="grey-7" key={key}>
                                                <LinkTextDisclaimer type={UscDisclaimerType.Finance} />
                                            </Text>
                                        );

                                    default:
                                        return (
                                            <Text tag="span" variant={textVariant} color="grey-7" key={key}>
                                                <span dangerouslySetInnerHTML={{ __html: value }} />{" "}
                                            </Text>
                                        );
                                }
                            })}
                        </Text>
                        <DisclaimerIconWrapper type={UscDisclaimerType.Finance} />
                    </>
                )}
            </Styles.MonthlyWrapper>
            {/* Euribor values are combined into a single label */}
            {!!euriborFinanceLabels?.length && (
                <Text tag="div" variant="body-small-regular" color="grey-7">
                    {euriborFinanceLabels.map(({ value }) => (
                        <span key={value}>{value}</span>
                    ))}
                </Text>
            )}
        </>
    );
};

export default UscMonthlyPriceDisclaimer;
